import React, { useState, useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";

const reasons = [
  {
    title: "A Culture of Belonging",
    content:
      "Every child feels seen, heard, and valued. We build confidence through respect, warmth, and emotional safety.",
  },
  {
    title: "Future-Ready Thinking",
    content:
      "Our teaching encourages curiosity, problem-solving, and independent thinking — skills required for tomorrow.",
  },
  {
    title: "Inspired Educators",
    content:
      "Highly qualified teachers who guide with empathy, experience, and continuous learning.",
  },
  {
    title: "Smart Learning Spaces",
    content:
      "Technology-enabled classrooms designed for engagement, creativity, and clarity.",
  },
  {
    title: "Safe & Secure Campus",
    content:
      "A disciplined and protected environment where parents feel confident and students feel free.",
  },
  {
    title: "Holistic Development",
    content:
      "Academics, values, fitness, creativity, and emotional intelligence — all equally important.",
  },
  {
    title: "AI-Powered Learning",
    content:
      "Personalized learning experiences supported by modern AI tools and analytics.",
  },
];

export default function AboutUs() {
  const [open, setOpen] = useState(null);

  useEffect(() => {
    AOS.init({
      duration: 900,
      easing: "ease-out-cubic",
      once: true,
      offset: 80,
    });
  }, []);

  return (
    <>
      <section className="bg-gradient-to-r from-blue-950 to-blue-900 py-24 text-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1
            className="text-4xl md:text-5xl font-normal tracking-wide"
            data-aos="fade-down"
          >
            About{" "}
            <span className="scholarVarsity">
              <span className="scholarVarsity">Fatehpur Public School</span>
            </span>
          </h1>

          <p
            className="mt-4 text-green-100 max-w-3xl mx-auto text-base md:text-lg"
            data-aos="fade-up"
            data-aos-delay="150"
          >
            Fatehpur Public School is a dedicated educational institution committed
            to providing quality education and fostering holistic development.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-3 py-20">
        <div className="text-center mb-14">
          <h2 className="text-4xl font-bold text-gray-900" data-aos="fade-up">
            About Our School
          </h2>

          <div
            className="w-28 h-1 bg-gradient-to-r from-green-600 to-indigo-600 mx-auto mt-4 rounded-full"
            data-aos="zoom-in"
            data-aos-delay="150"
          />
        </div>

        <div
          className="text-gray-700 leading-relaxed text-lg max-w-6xl mx-auto text-center space-y-6"
          data-aos="fade-up"
          data-aos-delay="250"
        >
          <p>
            <span className="scholarVarsity">
              <span className="scholarVarsity">Fatehpur Public School</span>
            </span>{" "}
            is a dedicated educational institution committed to providing quality
            education and fostering holistic development. Our mission is to nurture
            young minds, empower them with knowledge, and prepare them for a bright
            future.
          </p>

          <p>
            Founded with a vision to create responsible and confident individuals,{" "}
            <span className="scholarVarsity">
              <span className="scholarVarsity">Fatehpur Public School</span>
            </span>{" "}
            offers a nurturing environment where students can thrive academically,
            socially, and personally. Our experienced and dedicated faculty members
            work tirelessly to inspire curiosity, critical thinking, and a love for
            learning.
          </p>

          <p>
            The school has its own building in a lush green area and a big play
            ground. The main building comprises three floors and an indoor
            multi-purpose area. The School includes Science labs, Library, well
            equipped infirmary, Projector room etc. Every class room has a smart
            board with required software to promote learning potential of the
            students.
          </p>

          <p>
            At{" "}
            <span className="scholarVarsity">
              <span className="scholarVarsity">Fatehpur Public School</span>
            </span>
            , we believe in not only academic excellence but also in cultivating
            strong values, leadership qualities, and a sense of community. We
            encourage our students to explore their talents and interests through a
            wide range of extracurricular activities, ensuring a well-rounded
            education.
          </p>

          <p>
            Join us on this educational journey, where every child's potential is
            recognized and nurtured, and together, we create a brighter future for
            our students.
          </p>
        </div>
      </section>

      <section className="relative bg-gray-50 py-24 overflow-hidden" id="facilities">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(56,189,248,0.15),transparent_60%)]" />

        <div className="relative max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900" data-aos="fade-up">
              Why Parents Choose Us
            </h2>

            <p
              className="mt-4 text-gray-600"
              data-aos="fade-up"
              data-aos-delay="150"
            >
              More than education — a foundation for life
            </p>
          </div>

          <div
            className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto"
            data-aos="fade-up"
            data-aos-delay="200"
          >
            {reasons.map((item, index) => (
              <div
                key={index}
                onClick={() => setOpen(open === index ? null : index)}
                className={`cursor-pointer rounded-2xl border border-white/60 
                  bg-white/70 backdrop-blur-xl p-6 transition-all duration-500
                  hover:-translate-y-1 hover:shadow-xl
                  ${open === index ? "ring-2 ring-green-400 shadow-2xl" : ""}`}
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {item.title}
                  </h3>

                  <span className="text-green-600 text-2xl font-bold">
                    {open === index ? "–" : "+"}
                  </span>
                </div>

                <div
                  className={`transition-all duration-500 overflow-hidden ${
                    open === index
                      ? "max-h-[500px] mt-4 opacity-100"
                      : "max-h-0 opacity-100"
                  }`}
                >
                  <p className="text-gray-600 leading-relaxed">{item.content}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative bg-gray-50 py-24 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(56,189,248,0.15),transparent_60%)]" />

        <div className="relative max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900" data-aos="fade-up">
              Give Your Child the Education They Deserve
            </h2>

            <p
              className="mt-4 text-gray-600"
              data-aos="fade-up"
              data-aos-delay="150"
            >
              Because the right foundation today shapes a confident, capable
              future tomorrow.
            </p>
          </div>

          <p
            className="text-gray-700 leading-relaxed text-lg max-w-4xl mx-auto text-center"
            data-aos="fade-up"
            data-aos-delay="250"
          >
            In a rapidly evolving world, "standard" education isn't enough. Your
            child deserves to become a critical thinker with a lasting curiosity.
            At{" "}
            <span className="scholarVarsity">
              <span className="scholarVarsity">Fatehpur Public School</span>
            </span>
            , we provide a platform where dedicated teachers and community members
            work side-by-side to turn curiosity into confidence. Give your child
            the best foundation: an education that nurtures their potential today
            for the success of tomorrow.
          </p>
        </div>
      </section>
    </>
  );
}
